import { useSession } from "@/services/auth0/session";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

import type { RateBandBasisRow } from "./carrier-rate-bands";

const API_URL = import.meta.env.VITE_API_URL;

/**
 * Pricing-redesign: PickupRateBand admin client.
 *
 * Stores leg 1 of pickup pricing: sender area → DispatchCenter. Per
 * (pickupService × fromArea × dispatchCenter × weight bucket × validFrom).
 */
export interface PickupRateBand {
  id: string;
  carrierAddOnServiceId: string;
  carrierAddOnService?: { id: string; code: string; name: string };
  fromServiceAreaId: string;
  fromServiceArea?: { id: string; code: string; name: string };
  dispatchCenterId: string;
  dispatchCenter?: { id: string; code: string; name: string };
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  // Pricing-merger: smooth-pricing slope. NULL → flat band.
  perKgCents: number | null;
  anchorWeightKg: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode: string;
  validFrom: string;
  validTo?: string | null;
  active: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface CreatePickupRateBandInput {
  pickupServiceId: string;
  fromServiceAreaId: string;
  dispatchCenterId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validFrom: string;
  validTo?: string | null;
  active?: boolean;
}

export type UpdatePickupRateBandInput = Partial<CreatePickupRateBandInput>;

export function useGetPickupRateBands(params?: {
  carrierAddOnServiceId?: string;
  fromServiceAreaId?: string;
  dispatchCenterId?: string;
  active?: boolean;
}) {
  const { token } = useSession();

  return useQuery({
    queryKey: ["pickup-rate-bands", params],
    queryFn: async () => {
      const q = new URLSearchParams();
      if (params?.carrierAddOnServiceId)
        q.append("carrierAddOnServiceId", params.carrierAddOnServiceId);
      if (params?.fromServiceAreaId)
        q.append("fromServiceAreaId", params.fromServiceAreaId);
      if (params?.dispatchCenterId)
        q.append("dispatchCenterId", params.dispatchCenterId);
      if (params?.active !== undefined)
        q.append("active", String(params.active));

      const { data } = await axios.get<{
        success: boolean;
        data: PickupRateBand[];
      }>(
        `${API_URL}/api/v2/admin/pickup-rate-bands${q.toString() ? `?${q.toString()}` : ""}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      return data.data;
    },
    enabled: !!token,
  });
}

export function useCreatePickupRateBand(options?: {
  onSuccess?: (data: PickupRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (input: CreatePickupRateBandInput) => {
      const { data } = await axios.post<{
        success: boolean;
        data: PickupRateBand;
      }>(`${API_URL}/api/v2/admin/pickup-rate-bands`, input, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useUpdatePickupRateBand(options?: {
  onSuccess?: (data: PickupRateBand) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      id,
      data: updateData,
    }: {
      id: string;
      data: UpdatePickupRateBandInput;
    }) => {
      const { data } = await axios.patch<{
        success: boolean;
        data: PickupRateBand;
      }>(`${API_URL}/api/v2/admin/pickup-rate-bands/${id}`, updateData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
      });
      return data.data;
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}

export function useDeletePickupRateBand(options?: {
  onSuccess?: () => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (id: string) => {
      await axios.delete(`${API_URL}/api/v2/admin/pickup-rate-bands/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-rate-bands"] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
}

/**
 * Pricing-merger Phase 4: atomic-save mutation for the matrix editor.
 */
export interface AtomicSavePickupBandInput {
  fromServiceAreaId: string;
  dispatchCenterId: string;
  weightFromKg: number;
  weightToKg: number;
  baseRateCents: number;
  perKgCents?: number | null;
  anchorWeightKg?: number | null;
  rushHourSurchargeCents?: number | null;
  currencyCode?: string;
  validTo?: string | null;
  active?: boolean;
}

export interface AtomicSavePickupRateBandsInput {
  carrierAddOnServiceId: string;
  validFrom: string;
  /**
   * Scope of the slice this save owns. Server only deletes existing bands
   * whose (dispatchCenterId, fromServiceAreaId) match this declared slice,
   * and rejects bands whose own (dispatchCenterId, fromServiceAreaId) leak
   * outside the slice — prevents the workspace from silently wiping bands
   * the user can't even see.
   */
  dispatchCenterId: string;
  fromServiceAreaIds: string[];
  bands: AtomicSavePickupBandInput[];
  /**
   * Required — the stored rows for THIS slice, as read. The Server refuses
   * with 409 if the slice has moved since, rather than overwriting whatever
   * another editor saved in the meantime. See `RateBandBasisRow`.
   */
  basis: RateBandBasisRow[];
  intentionalDiscontinuity?: boolean;
}

export interface PickupContinuityViolation {
  upperBandWeightFromKg: number;
  expectedBaseRateCents: number;
  actualBaseRateCents: number;
  diffCents: number;
}

export class PickupBandContinuityViolationError extends Error {
  constructor(
    public violations: PickupContinuityViolation[],
    message: string
  ) {
    super(message);
    this.name = "PickupBandContinuityViolationError";
  }
}

export function useAtomicSavePickupRateBands(options?: {
  onSuccess?: (data: PickupRateBand[]) => void;
  onError?: (error: Error) => void;
}) {
  const { token } = useSession();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      input: AtomicSavePickupRateBandsInput
    ): Promise<PickupRateBand[]> => {
      try {
        const { data } = await axios.post<{
          success: boolean;
          data: PickupRateBand[];
        }>(`${API_URL}/api/v2/admin/pickup-rate-bands/atomic-save`, input, {
          headers: { Authorization: `Bearer ${token}` },
        });
        return data.data;
      } catch (err) {
        if (axios.isAxiosError(err) && err.response?.status === 400) {
          const body = err.response.data as {
            error?: string;
            details?: {
              message?: string;
              violations?: PickupContinuityViolation[];
            };
          };
          if (body?.error === "BAND_CONTINUITY_VIOLATION") {
            throw new PickupBandContinuityViolationError(
              body.details?.violations ?? [],
              body.details?.message ?? "Band continuity violation"
            );
          }
        }
        throw err;
      }
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ["pickup-rate-bands"] });
      options?.onSuccess?.(data);
    },
    onError: options?.onError,
  });
}
